import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { selectUserSubscriptionUniqueId } from '../../../../../auth/AuthSelectors';
import { ContentEditSegment } from '../../../../../auth/AuthTypes';
import { Button } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Button/Button';
import {
  selectContentEditSegment,
  selectVideoEditorModalStockItem,
} from '../../../../ContentEdit/ContentEditSlice';
import EnterpriseContactModal from './EnterpriseContactModal';
import { useVideoUI, useVideoSelection } from './context/VideoEditorContext';
import { handlePurchaseCredits } from './utils/purchaseUtils';

const useGenerateButtonLogic = (onGenerateVideo: () => void) => {
  const contentEditSegment = useSelector(
    selectContentEditSegment
  ) as ContentEditSegment;
  const userSubscriptionUniqueId = useSelector(selectUserSubscriptionUniqueId);
  const selectedStockItem = useSelector(selectVideoEditorModalStockItem);
  const { promptInputRef, selectedUseCase, defaultPlaceholder } = useVideoUI();
  const { selection } = useVideoSelection();

  const buttonConfig = useMemo(() => {
    const config: Record<
      ContentEditSegment,
      { action: () => void; text: string }
    > = {
      unsubscribed: {
        action: () => {
          Telemetry.increment('ai.contentEdit.buttonClick.pricingRedirect');
          window.location.href = '/pricing';
        },
        text: 'Subscribe to Generate',
      },
      subscribed_user: {
        action: () => {
          onGenerateVideo();
        },
        text: 'Generate Video',
      },
      upgradable_user: {
        action: () => {
          handlePurchaseCredits({
            selectedStockItem,
            selection,
            selectedUseCase,
            defaultPlaceholder,
            promptText: promptInputRef.current?.getPromptValue() || '',
            planId: userSubscriptionUniqueId,
          }); // Redirect to upgrade
        },
        text: 'Upgrade to Generate',
      },
      enterprise_no_permission: {
        action: () => {},
        text: 'Upgrade to Generate',
      },
      disabled: {
        action: () => {},
        text: 'Disabled',
      },
    };

    return config[contentEditSegment];
  }, [
    contentEditSegment,
    onGenerateVideo,
    selectedStockItem,
    selection,
    selectedUseCase,
    defaultPlaceholder,
    userSubscriptionUniqueId,
  ]);

  return { buttonConfig };
};

interface GenerateButtonProps {
  disabled: boolean;
  onGenerateVideo: () => void;
  className?: string;
  buttonClassName?: string;
}

export default function GenerateButton({
  disabled,
  onGenerateVideo,
  className,
  buttonClassName,
}: GenerateButtonProps) {
  const { buttonConfig } = useGenerateButtonLogic(onGenerateVideo);
  const contentEditSegment = useSelector(selectContentEditSegment);
  const [isEnterpriseModalOpen, setIsEnterpriseModalOpen] = useState(false);

  const handleClick = () => {
    if (contentEditSegment === 'enterprise_no_permission') {
      setIsEnterpriseModalOpen(true);
      return;
    }
    buttonConfig.action();
  };

  return (
    <Button
      disabled={disabled}
      className={className}
      buttonClassName={twMerge('h-12 hover:bg-yellow-500', buttonClassName)}
      onClick={handleClick}
    >
      {buttonConfig.text}
      <EnterpriseContactModal
        isOpen={isEnterpriseModalOpen}
        onClose={() => setIsEnterpriseModalOpen(false)}
      />
    </Button>
  );
}
